import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { 
  CumulativeChartDataDto, 
  EntityChartDataDto, 
  ChartSummaryDto, 
  ChartFilterParams,
  ChartEntityType,
  ChartGranularity,
  DateRangePreset,
  CumulativeInsightsChartDataDto
} from '../models/chart.models';

@Injectable({
  providedIn: 'root'
})
export class ChartService {
  private readonly baseUrl = `${environment.apiConfig.uri}/api/Chart`;

  constructor(private http: HttpClient) {}

  /**
   * Get cumulative chart data for all entities
   */
  getCumulativeChartData(filters?: ChartFilterParams): Observable<CumulativeChartDataDto> {
    const params = this.buildHttpParams(filters);
    return this.http.get<CumulativeChartDataDto>(`${this.baseUrl}/cumulative`, { params });
  }

  /**
   * Get chart data for a specific entity type
   */
  getEntityChartData(entityType: ChartEntityType, filters?: ChartFilterParams): Observable<EntityChartDataDto> {
    const params = this.buildHttpParams(filters);
    return this.http.get<EntityChartDataDto>(`${this.baseUrl}/${entityType}`, { params });
  }

  /**
   * Get summary statistics
   */
  getChartSummary(geoZoneIds?: string, agentIds?: string): Observable<ChartSummaryDto> {
    let params = new HttpParams();
    if (geoZoneIds) {
      params = params.set('geoZoneIds', geoZoneIds);
    }
    if (agentIds) {
      params = params.set('agentIds', agentIds);
    }
    return this.http.get<ChartSummaryDto>(`${this.baseUrl}/summary`, { params });
  }

  /**
   * Get scrap collection insights chart data
   */
  getInsightsChartData(filters?: ChartFilterParams): Observable<CumulativeInsightsChartDataDto> {
    const params = this.buildHttpParams(filters);
    return this.http.get<CumulativeInsightsChartDataDto>(`${this.baseUrl}/insights`, { params });
  }

  /**
   * Build HTTP params from filter object
   */
  private buildHttpParams(filters?: ChartFilterParams): HttpParams {
    let params = new HttpParams();
    
    if (!filters) return params;

    if (filters.startDate) {
      params = params.set('startDate', filters.startDate);
    }
    if (filters.endDate) {
      params = params.set('endDate', filters.endDate);
    }
    if (filters.granularity !== undefined) {
      params = params.set('granularity', filters.granularity.toString());
    }
    if (filters.geoZoneIds) {
      params = params.set('geoZoneIds', filters.geoZoneIds);
    }
    if (filters.agentIds) {
      params = params.set('agentIds', filters.agentIds);
    }

    return params;
  }

  /**
   * Get date range based on preset
   */
  getDateRangeFromPreset(preset: DateRangePreset): { startDate: Date; endDate: Date } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (preset) {
      case 'today':
        return { startDate: today, endDate: today };
      
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { startDate: yesterday, endDate: yesterday };
      }
      
      case 'last7days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        return { startDate: start, endDate: today };
      }
      
      case 'last30days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        return { startDate: start, endDate: today };
      }
      
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { startDate: start, endDate: today };
      }
      
      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        return { startDate: start, endDate: end };
      }
      
      case 'thisQuarter': {
        const quarter = Math.floor(now.getMonth() / 3);
        const start = new Date(now.getFullYear(), quarter * 3, 1);
        return { startDate: start, endDate: today };
      }
      
      case 'thisYear': {
        const start = new Date(now.getFullYear(), 0, 1);
        return { startDate: start, endDate: today };
      }
      
      default:
        return { startDate: today, endDate: today };
    }
  }

  /**
   * Format date for API request (ISO string)
   */
  formatDateForApi(date: Date): string {
    return date.toISOString();
  }

  /**
   * Get granularity label for display
   */
  getGranularityLabel(granularity: ChartGranularity): string {
    switch (granularity) {
      case ChartGranularity.Daily:
        return 'Daily';
      case ChartGranularity.Weekly:
        return 'Weekly';
      case ChartGranularity.Monthly:
        return 'Monthly';
      default:
        return 'Daily';
    }
  }

  /**
   * Get preset label for display
   */
  getPresetLabel(preset: DateRangePreset): string {
    const labels: Record<DateRangePreset, string> = {
      'today': 'Today',
      'yesterday': 'Yesterday',
      'last7days': 'Last 7 Days',
      'last30days': 'Last 30 Days',
      'thisMonth': 'This Month',
      'lastMonth': 'Last Month',
      'thisQuarter': 'This Quarter',
      'thisYear': 'This Year',
      'custom': 'Custom Range'
    };
    return labels[preset];
  }

  /**
   * Generate default colors for chart entities
   */
  getDefaultColors(): string[] {
    return [
      '#10b981', // Green (primary)
      '#3b82f6', // Blue
      '#f59e0b', // Amber
      '#ef4444', // Red
      '#8b5cf6', // Purple
      '#06b6d4', // Cyan
      '#ec4899', // Pink
      '#84cc16', // Lime
      '#f97316', // Orange
      '#6366f1', // Indigo
    ];
  }
}

